
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
// Image removed - use img tag;
import { Link } from "react-router-dom";
import { heroApi } from "@/lib/api";
import toast from "react-hot-toast";

interface HeroCollection {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  gradient: string | null;
  emoji: string | null;
  filtervalue: string | null;
  display_order: number;
  is_active: boolean;
}

export default function CollectionFlipCards() {
  const [collections, setCollections] = useState<HeroCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [announcementHeight, setAnnouncementHeight] = useState(0);

  useEffect(() => {
    const fetchHeroCollections = async () => {
      try {
        setLoading(true);
        const response = await heroApi.getFeaturedCollections();
        setCollections(response.collections || []);
        // Reset index if collections change
        setCurrentIndex(0);
      } catch (error) {
        console.error("Error fetching hero collections:", error);
        toast.error("Failed to load collections");
        // Fallback to empty array
        setCollections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroCollections();
  }, []);

  // Calculate announcement bar height
  useEffect(() => {
    const updateAnnouncementHeight = () => {
      const announcementBar = document.querySelector('[data-announcement-bar]');
      if (announcementBar) {
        setAnnouncementHeight(announcementBar.clientHeight);
      } else {
        setAnnouncementHeight(0);
      }
    };
    updateAnnouncementHeight();
    const interval = setInterval(updateAnnouncementHeight, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (collections.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % collections.length);
    }, 6000); // Change card every 6 seconds

    return () => clearInterval(interval);
  }, [collections.length]);

  const minHeight = 500 - announcementHeight;

  if (loading) {
    return (
      <div className="relative h-full w-full flex items-center justify-center" style={{ minHeight: `${Math.max(350, minHeight)}px` }}>
        {/* Skeleton Card Container */}
        <div className="relative w-full max-w-lg aspect-[3/4]">
          <div className="relative h-full w-full rounded-3xl overflow-hidden bg-gray-200 animate-pulse shadow-2xl">
            {/* Skeleton Image */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400" />
            
            {/* Skeleton Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-end p-8 z-10">
              <div className="h-8 w-48 bg-gray-300 rounded mb-6 animate-pulse" />
              <div className="h-12 w-40 bg-gray-300 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (collections.length === 0) {
    return null;
  }

  return (
    <div className="relative h-full w-full flex items-center justify-center" style={{ minHeight: `${Math.max(400, minHeight)}px` }}>
      {/* Card Container */}
      <div className="relative w-full max-w-lg aspect-[3/4]">
        <AnimatePresence mode="wait">
          {collections.map((collection, index) => {
            if (index !== currentIndex) return null;

            // Default gradient if not provided
            const gradient = collection.gradient || "from-aarsa-sage-500 to-aarsa-gold-500";
            const imageUrl = collection.image_url || "/product1.png";

            return (
              <motion.div
                key={collection.id}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
                className="absolute inset-0"
              >
                <Link to={collection.filtervalue ? `/products?${collection.filtervalue}` : `/products?collection=${collection.slug}`}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className={`relative h-full w-full rounded-3xl overflow-hidden bg-gradient-to-br ${gradient} shadow-2xl cursor-pointer`}
                  >
                    {/* Product Image */}
                    <div className="absolute inset-0">
                      <img
                        src={imageUrl}
                        alt={collection.name}
                        className="object-cover"
                      />
                      {/* Overlay for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    </div>

                    {/* Card Content */}
                    <div className="absolute inset-0 flex flex-col items-center justify-end p-8 text-white z-10">
                      {/* Collection Name */}
                      <motion.h3
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-xl md:text-2xl font-medium font-playfair mb-6 text-center"
                      >
                        {collection.name}
                      </motion.h3>

                      {/* Explore Button */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="px-6 py-3 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-colors"
                      >
                        <span className="text-sm font-semibold">Explore Collection</span>
                      </motion.div>
                    </div>

                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12" />
                  </motion.div>
                </Link>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
